import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { connect } from 'react-redux';
import {
  useLocation,
  useRouteMatch,
  withRouter,
} from 'react-router-dom';
import { Container } from 'reactstrap';

import { showModal } from '../../actions';
import {
  addSelectedCollection,
  removeSelectedCollection,
  clearSelectedCollections,
  clearCollection,
  clearCollections,
  setCollectionsParams,
  clearSelectedCollectionFilters,
} from '../../actions/Collections';
import {
  addSelectedFile,
  removeSelectedFile,
  clearSelectedFiles,
  clearSelectedFilters,
  clearFilesList,
  clearFilesFilters,
  setFilesGroupUuid,
  clearFilesGroup,
} from '../../actions/Files';
import {
  setSearchFields,
  setSearchQueryType,
  setSearchTerm,
} from '../../actions/Search';

import { APIgetFile } from '../../api/Files';

import { getDefaultSortParams } from '../../helpers/Sort';

import CollectionAddModal from '../../components/Collections/AddModalV2';
import FilesHeader from '../../components/Files/Header';
import FilesHeaderBackLink from '../../components/Files/HeaderBackLink';
import FilesFilters from '../../components/Files/Filters';
import ArchiveHeader from '../../components/ArchiveHeader';
import ArchiveSearchHeader from '../../components/ArchiveSearchHeader';
import CollectionPreview from '../../components/CollectionPreview';
import CustomDragLayer from '../../components/CustomDragLayer';
import CollectionResultsSection from './CollectionResultsSection';
import FileResultsSection from './FileResultsSection';
import MiniHeader from '../../mini/components/Header';
import Loading from '../../components/Loading';
import EmptySearch from '../EmptyContainers/Search';
import TrashHeader from '../../components/TrashHeader';

import {
  isMini,
  isArchive,
  isTrash,
} from '../../helpers/App';
import ItemTypes from '../../helpers/DNDItemTypes';

import SelectionArea from '../../lib/viselect/viselect';

const SearchResults = ({
  dispatch,
  files,
  filesTotal,
  filesLoading,
  search,
  sidebarCollectionOpen,
  selectedFiles,
  selectedCollections,
  collection,
  collections,
  collectionsTotal,
  collectionsLoading,
}) => {
  const location = useLocation();
  const match = useRouteMatch();
  const [relatedFile, setRelatedFile] = useState(null);
  const dsRef = useRef(null);

  const loadRelatedFile = useCallback(() => {
    if (!match.params.relatedFileId) {
      setRelatedFile(null);
    } else {
      APIgetFile(match.params.relatedFileId).then((response) => {
        if (response && response.data) {
          setRelatedFile(response.data);
        } else {
          setRelatedFile(null);
        }
      });
    }
  }, [match.params.relatedFileId]);

  useEffect(() => {
    if (
      !relatedFile ||
      (match.params.relatedFileId && relatedFile.uuid !== match.params.relatedFileId)
    ) {
      loadRelatedFile();
    }
  }, [match.params.relatedFileId, relatedFile, loadRelatedFile]);

  useEffect(() => {
    if (!dsRef.current && !isMini()) {
      dsRef.current = new SelectionArea({
        selectables: [
          '.files-grid > .grid-item',
          'table.files-list tr',
          '.files-masonry .masonry-item',
          '.collections > .collections-item',
          'table.collections-list tr',
        ],
        startareas: [
          '.files-grid',
          '.files-list',
          '.files-masonry',
          '.collections',
        ],
        boundaries: ['.draggable-container'],
        container: '.draggable-container',
        behaviour: {
          overlap: 'keep',
        },
        features: {
          touch: false,
          singleTap: {
            allow: false,
          },
        },
      })
        .on('beforestart', ({ event }) => {
          if (
            event.target.closest('.collections-item') ||
            event.target.closest('.grid-item') ||
            event.target.closest('.masonry-item') ||
            event.target.closest('.list-item')
          ) {
            return false;
          }
          return true;
        })
        .on('start', ({ event }) => {
          if (!event.metaKey) {
            dispatch(clearSelectedFiles());
            dispatch(clearSelectedCollections());
          }
        })
        .on('move', ({ store }) => {
          const { added, removed } = store.changed;
          added.forEach((s) => {
            const fileUuids = files?.find(f => (f.uuid === s.id));
            const collectionUuids = collections?.find(c => (c.uuid === s.id));
            if (fileUuids) {
              dispatch(addSelectedFile(fileUuids));
            }
            if (collectionUuids) {
              dispatch(addSelectedCollection(collectionUuids));
            }
          });
          removed.forEach((s) => {
            dispatch(removeSelectedFile(s.id));
            dispatch(removeSelectedCollection(s.id));
          });
        })
        .on('stop', () => {
          dsRef.current.clearSelection();
        });
    }

    return () => {
      if (dsRef.current) {
        dsRef.current.destroy();
        dsRef.current = null;
      }
    };
  }, [files, collections, relatedFile, dispatch]);

  const clearFiles = useCallback(() => {
    if (
      !search ||
      (location.state && location.state.fromCollectionPreview)
    ) {
      dispatch(setSearchTerm(''));
      dispatch(clearFilesFilters());
      dispatch(clearSelectedFilters());
    }
    if (search) {
      dispatch(clearFilesList());
    } else {
      dispatch(clearFiles());
    }
  }, [search, location.state, dispatch]);

  const handleMoveSelectedItems = (targetCollectionUuid, droppedItem) => {
    const filesUuid = selectedFiles.map(sf => (sf.uuid));
    const collectionsUuid = selectedCollections.map(sc => (sc.uuid));
    if (filesUuid.length === 0 && collectionsUuid.length === 0) {
      if (droppedItem.type === ItemTypes.FILE) {
        filesUuid.push(droppedItem.id);
      }
      if (droppedItem.type === ItemTypes.COLLECTION) {
        collectionsUuid.push(droppedItem.id);
      }
    }

    dispatch(showModal('MoveItemsModal', {
      className: 'modal-move-items',
      targetCollectionUuid,
      currentCollectionUuid: collection ? collection.uuid : null,
      collections: collectionsUuid,
      files: filesUuid,
      backdrop: false,
    }));
  };

  useEffect(() => () => {
    dispatch(setSearchQueryType(null));
    dispatch(setSearchTerm(null));
    dispatch(setSearchFields([]));
    dispatch(clearSelectedCollectionFilters());
    dispatch(clearSelectedFilters());
    dispatch(clearFilesFilters());
    dispatch(clearFilesList());
    dispatch(clearCollection());
    const sortParams = getDefaultSortParams('collections', 'position', 'asc');
    dispatch(clearCollections(sortParams));
    dispatch(setCollectionsParams({
      collectionUuid: null,
    }));
    dispatch(setFilesGroupUuid(null));
    dispatch(clearFilesGroup());
  }, [dispatch]);

  return (
    <div className={classnames('main-content', 'split-content', 'page-files', {
      'split-content-active': sidebarCollectionOpen && !isArchive(),
      'archive-content': isArchive(),
      'trash-content': isTrash(),
    })}
    >
      {!isMini() && (
        <>
          <ArchiveHeader />
          <TrashHeader />
        </>
      )}
      {!isMini() && search && (
        <ArchiveSearchHeader />
      )}
      <div className="split-content-wrapper search-results">
        <Container fluid className="draggable-container">
          {isMini() ? (
            <>
              <MiniHeader />
              <FilesFilters />
            </>
          ) : (
            <>
              <FilesHeader relatedFile={relatedFile} />
              <FilesHeaderBackLink />
              <FilesFilters />
            </>
          )}
          {collectionsTotal > 0 && (
            <CollectionResultsSection handleMoveSelectedItems={handleMoveSelectedItems} />
          )}
          {filesTotal > 0 && (
            <FileResultsSection handleMoveSelectedItems={handleMoveSelectedItems} />
          )}
          {(
            (collectionsLoading || filesLoading) &&
            (filesTotal === 0 || collectionsTotal === 0)
          ) && (
            <Loading />
          )}

          {(
            !(collectionsLoading || filesLoading) &&
            (filesTotal === 0 && collectionsTotal === 0)
          ) && (
            <EmptySearch />
          )}
          <CollectionAddModal />
        </Container>
        <CollectionPreview />
        {!isMini() && (
          <CustomDragLayer />
        )}
      </div>
    </div>
  );
};

const mapStateToProps = state => ({
  search: state.Search.search,
  files: state.Files.files,
  filesTotal: state.Files.filesTotal,
  filesLoading: state.Files.loading,
  selectedFiles: state.Files.selectedFiles,
  collection: state.Collections.collection,
  collections: state.Collections.collections,
  collectionsTotal: state.Collections.collectionsTotal,
  collectionsLoading: state.Collections.collectionsLoading,
  selectedCollections: state.Collections.selectedCollections,
  sidebarCollectionOpen: state.Collections.sidebarCollectionOpen,
});

SearchResults.propTypes = {
  search: PropTypes.string,
  dispatch: PropTypes.func.isRequired,
  files: PropTypes.array.isRequired,
  filesTotal: PropTypes.number.isRequired,
  filesLoading: PropTypes.bool.isRequired,
  collectionsTotal: PropTypes.number.isRequired,
  selectedFiles: PropTypes.array.isRequired,
  selectedCollections: PropTypes.array.isRequired,
  sidebarCollectionOpen: PropTypes.bool.isRequired,
  collection: PropTypes.object,
  collections: PropTypes.array.isRequired,
  collectionsLoading: PropTypes.bool.isRequired,
};

SearchResults.defaultProps = {
  search: null,
  collection: null,
};

export default withRouter(connect(mapStateToProps)(SearchResults));
